// @ts-check
import { gql } from '@apollo/client/core';
import { Image } from '@react-pdf/renderer';
import React from 'react';
import { Box, Flex, QRCode, Text, Title } from '../../pdf-design-system';
import sourceSans400 from '../../pdf-design-system/fonts/source-sans-pro/400';
import openSans600 from '../../pdf-design-system/fonts/open-sans/600';
import AveryL7169, { LabelItem } from '../AveryTemplates/AveryL7169';

/**
 *
 * @param {object} props
 * @param {string} props.locale
 * @param {object} props.data
 * @param {object} props.data.collection
 * @param {object} props.data.collection.site
 * @param {string} props.data.collection.site.publicUrl
 * @param {object} props.data.collection.logo
 * @param {object} props.data.collection.logo.fixed
 * @param {string} props.data.collection.logo.fixed.src
 * @param {object[]} props.data.sections
 * @param {boolean} [props.border=false] Show the outline for the label. Useful for debugging
 * @returns
 */
export default function ExhibitorQrCodeLabelsL7169({
  data,
  locale,
  border,
  ...rest
}) {
  const { collection } = data;
  const sections = afterQuery(data, rest);

  return (
    <AveryL7169 theme={theme}>
      {sections.map((section) => {
        return (
          <LabelItem border={border}>
            <Flex
              style={{
                width: '100%',
                padding: '2mm',
                paddingVertical: '7mm',
                flexDirection: 'column',
                alignItems: 'center',
                height: '139mm',
              }}
            >
              <Box style={{ marginTop: '2mm' }}>
                {section.registration.profile.logo?.fixed?.src ? (
                  <Image
                    src={section.registration.profile.logo?.fixed?.src}
                    style={{ width: '7.5cm' }}
                  />
                ) : (
                  <Image
                    src={collection.logo.fixed.src}
                    style={{ width: '4cm' }}
                  />
                )}
              </Box>
              <Box style={{ flexGrow: 1 }} />
              <Flex
                style={{
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Title style={{ marginBottom: '5mm' }}>
                  Scan to make notes
                </Title>
                <Flex>
                  <Flex
                    style={{
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingTop: '5mm',
                      marginRight: '2mm',
                    }}
                  >
                    <Image
                      src={collection.logo.fixed.src}
                      style={{ height: '2.3cm', flexGrow: 0, flexShrink: 0 }}
                    />

                    <Image
                      src="https://res.cloudinary.com/bottlebooks/image/upload/v1654310405/pdf-objects/Bottlebooks/bottlebooks-logo-vertical.png"
                      style={{ height: '2.3cm', flexGrow: 0, flexShrink: 0 }}
                    />
                  </Flex>
                  <QRCode
                    margin={0}
                    url={`${collection.site.publicUrl}/e/${section.registration.companyId}`}
                    style={{
                      height: '5.0cm',
                      width: '5.0cm',
                      marginTop: '5mm',
                      marginLeft: '4mm',
                    }}
                  />
                </Flex>
              </Flex>
              <Text style={{ position: 'absolute', top: '2mm', left: '2mm' }}>
                {section.registration.stand?.name}
              </Text>
            </Flex>
          </LabelItem>
        );
      })}
    </AveryL7169>
  );
}

/**
 * @typedef {import('../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"] & {
 *  colors?: Record<string, string>;
 * }} Theme
 */

/** @type {Theme} */
export const theme = {
  components: {
    Text: {
      base: { ...sourceSans400 },
      default: { fontSize: 10, lineHeight: 7.8 / 6.5 },
    },
    Title: {
      base: { ...openSans600 },
      default: {
        fontSize: 24,
        lineHeight: 1,
      },
    },
  },
};

ExhibitorQrCodeLabelsL7169.fragment = gql`
  fragment ExhibitorQrCodeLabelsL7169 on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    collection {
      collectionId
      eventId: collectionId
      site {
        publicUrl
      }
      logo {
        # 55mm in 300dpi
        fixed(width: 654, crop: PAD, gravity: NORTH, fetch_format: JPG) {
          src
        }
      }
    }
    sections {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
          name
          profile {
            logo {
              # 55mm in 300dpi
              fixed(
                width: 400
                height: 250
                crop: PAD
                gravity: CENTER
                fetch_format: JPG
              ) {
                src
              }
            }
          }
          stand {
            name
          }
        }
      }
    }
  }
`;

function afterQuery(data, rest) {
  if (rest.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === rest.companyId
    );
  }
  if (rest.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === rest.registrationId
    );
  }

  return data.sections;
}
